import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { addYears } from 'date-fns';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionHeader,
  AccordionTitle,
} from 'telia-front-react/accordion';
import { Card, CardContent } from 'telia-front-react/card';
import { Grid, GridColumn } from 'telia-front-react/grid';
import { Text } from 'telia-front-react/text';
import { TextField } from 'telia-front-react/textfield';
import { Chip } from 'telia-front-react/chip';
import { Filter } from 'telia-front-react/filter';
import { FilterHeader } from 'telia-front-react/filter';
import { Button } from 'telia-front-react/button';
import { Toggle } from 'telia-front-react/toggle';
import { ChoiceGroup } from 'telia-front-react/choice-group';
import { DateField } from 'telia-front-react/datefield';
import { Separator } from 'telia-front-react/separator';

import TranslateStore from '@stores/TranslateStore';
import FilterStore from '@stores/FilterStore';

interface DisturbanceFilterProps {
  isDealer: boolean;
  customerId: number;
  filterStore?: FilterStore;
  translateStore?: TranslateStore;
}

class DisturbanceFilter extends React.Component<DisturbanceFilterProps> {
  private FILTER_RANGE = {
    from: addYears(new Date(), -1),
    to: addYears(new Date(), 1)
  };

  private selectClient() {
    const { filterStore } = this.props;
    filterStore.updateClient(true);
  }

  public componentDidMount() {
    const { customerId, isDealer, filterStore } = this.props;
    if (!isDealer && customerId) {
      this.selectClient();
    }
    if (isDealer) {
      filterStore.setStatusCurrent(true);
    }
  }

  public componentWillUnmount() {
    const { filterStore } = this.props;
    filterStore.resetFilters(true);
  }

  public componentDidUpdate(prevProps: DisturbanceFilterProps) {
    const { customerId, filterStore } = this.props;
    if (prevProps.customerId && !customerId) {
      filterStore.updateClient(false);
    } else if (prevProps.customerId && customerId && prevProps.customerId !== customerId) {
      this.selectClient();
    }
  }

  public render(): JSX.Element {
    const {
      translateStore: { translate },
      filterStore: { isOpen, toggleFilter, resetFilters },
      isDealer
    } = this.props;

    return (
      <Card>
        <CardContent padding="small">
          <Filter
            isOpen={isOpen}
            onToggle={toggleFilter}
            title={translate('filter.filters')}
            heading={{level: 2}}
            saveButton={{
              text: translate('filter.search'),
              onClick: () => toggleFilter()
            }}
            clearButton={{
              text: translate('filter.clear'),
              link: false,
              block: true,
              iconLeft: '#close',
              variant: 'secondary',
              onClick: () => resetFilters(false, true, true)
            }}
          >
            {isDealer && (
              <>
                {this.getClientFilter()}
                {this.getElementFilter()}
                {this.getSegmentFilter()}
                {this.getStatusFilter()}
              </>
            )}
            {this.getStartFilter()}
            {this.getCountyFilter()}
          </Filter>
        </CardContent>
      </Card>
    );
  }

  private getClientFilter(): JSX.Element {
    const {
      filterStore: { getClient, selectedClientOnly, updateClient },
      customerId
    } = this.props;

    return (
      customerId && (
        <Text>
          <Toggle
            label={getClient.label}
            id="client"
            defaultChecked={getClient.defaultChecked}
            checked={selectedClientOnly}
            onChange={(checked: boolean) => updateClient(checked)}
          />
          <Separator spacing="xxsmall" weight="thin" color="invisible" />
        </Text>
      )
    );
  }

  private getElementFilter(): JSX.Element {
    const {
      translateStore: { translate },
      filterStore: { elementInput, selectedElements, addElement, updateElementInput, removeElement }
    } = this.props;

    return (
      <div>
        <Separator spacing="xxsmall" weight="thin" color="invisible" />
        <Text>
          <form
            onSubmit={(event: React.FormEvent) => addElement(event)}
          >
            <Grid gutter="small" noWrap={true} align={['bottom-xs']}>
              <GridColumn width={['max']}>
                <TextField
                  id="add-element"
                  label={translate('filter.element')}
                  value={elementInput}
                  size="medium"
                  onChange={(element: string) => updateElementInput(element)}
                />
              </GridColumn>
              <GridColumn width={['min']}>
                <Button
                  icon="#search"
                  type="employee"
                  text={translate('filter.add')}
                  size="default"
                  attributes={{
                    type: 'submit',
                  }}
                />
              </GridColumn>
            </Grid>
          </form>
          {selectedElements.length > 0 && (
            <Grid gutter="small">
              {selectedElements.map((element, key) => (
                <GridColumn width={['xs']} key={key}>
                  <Chip className="primary-4" onRemove={() => removeElement(element)}>
                    {element}
                  </Chip>
                </GridColumn>
              ))}
            </Grid>
          )}
        </Text>
      </div>
    );
  }

  private getSegmentFilter(): JSX.Element {
    const {
      translateStore: { translate },
      filterStore: { getInternal, getArea, selectedInternalOnly, selectedAreas, updateInternal, updateArea }
    } = this.props;

    return (
      <>
        <Separator spacing="small" weight="thin" color="medium" />
        <Text>
          <Grid>
            <GridColumn width={['xs']}>
              <b>{translate('filter.segment')}</b>
            </GridColumn>
          </Grid>
          <ChoiceGroup
            id="area"
            options={getArea}
            type="check"
            label={translate('filter.segment')}
            hideLabel={true}
            value={selectedAreas.map((a) => a.value)}
            onChange={(value: any) => updateArea(value as string[])}
          />
        </Text>
        <Separator spacing="xxsmall" weight="thin" color="invisible" />
        <ChoiceGroup
          id="internal"
          options={getInternal}
          type="check"
          label={translate('filter.internal')}
          hideLabel={true}
          value={selectedInternalOnly.map((i) => i.value)}
          onChange={(value: any) => updateInternal(value as string[])}
        />
      </>
    );
  }

  private getStatusFilter(): JSX.Element {
    const {
      translateStore: { translate },
      filterStore: { getStatus, selectedStatuses, updateStatus }
    } = this.props;

    return (
      <div>
        <Separator spacing="small" weight="thin" color="medium" />
        <Text>
          <Grid>
            <GridColumn width={['xs']}>
              <b>{translate('filter.status.start')}</b>
            </GridColumn>
          </Grid>
          <ChoiceGroup
            id="status"
            label={translate('filter.status')}
            hideLabel={true}
            type="check"
            options={getStatus}
            value={selectedStatuses.map((s) => s.value)}
            onChange={(value: any) => updateStatus(value as string[])}
          />
        </Text>
        <Separator spacing="xsmall" weight="thin" color="invisible" />
      </div>
    );
  }

  private getStartFilter(): JSX.Element {
    const {
      translateStore: { translate },
      filterStore: { startFrom, startUntil, getCurrentStatus, selectedCurrentStatus, updateCurrentStatus, updateFromDate, updateUntilDate },
      isDealer
    } = this.props;

    return (
      <FilterHeader>
        {isDealer && <Separator spacing="small" weight="thin" color="medium" />}
        <Text>
          <b>{translate('filter.start.range')}</b>
          <div>
            {isDealer && (
              <>
                <Toggle
                  label={translate('filter.status.current')}
                  id="current"
                  defaultChecked={getCurrentStatus.defaultChecked}
                  checked={selectedCurrentStatus}
                  onChange={(checked: boolean) => updateCurrentStatus(checked)}
                />
                <Separator spacing="xxsmall" weight="thin" color="invisible" />
              </>
            )}
            <DateField
              label={translate('filter.start.from')}
              id="startdate-from"
              value={startFrom}
              onChange={updateFromDate}
              datepicker={{
                range: this.FILTER_RANGE
              }}
            />
            <Separator spacing="xxsmall" weight="thin" color="invisible" />
            <DateField
              label={translate('filter.start.to')}
              id="startdate-until"
              value={startUntil}
              onChange={updateUntilDate}
              datepicker={{
                range: this.FILTER_RANGE
              }}
            />
          </div>
        </Text>
      </FilterHeader>
    );
  }

  private getCountyFilter(): JSX.Element {
    const {
      translateStore: { translate },
      filterStore: { getRegion, selectedRegions, updateRegions },
      isDealer
    } = this.props;

    return (
      <Accordion
        defaultOpenItem={!isDealer ? ['filter-county'] : null}
        className="country-filter"
      >
        <AccordionItem id="filter-county">
          <AccordionHeader>
            <AccordionTitle>{translate('filter.county')}</AccordionTitle>
          </AccordionHeader>
          <AccordionContent>
            <ChoiceGroup
              id="county"
              label={translate('filter.county')}
              hideLabel={true}
              type="check"
              options={getRegion}
              value={selectedRegions.map((r) => r.value)}
              onChange={(value: any) => updateRegions(value as string[])}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }
}

export default inject(
  'isDealer',
  'customerId',
  'filterStore',
  'translateStore',
)(observer(DisturbanceFilter));
